import React, { useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../../shared/hooks/useAuthQuery';
import { api } from '../../../../../convex/_generated/api';
import { Spinner } from '../../../../../catalyst-components/spinner';
import { DailyStatsChart } from '../../../../../shared/components/DailyStatsChart';
import { DistributionHistogram } from './DistributionHistogram';
import { HorizontalBarChart } from './HorizontalBarChart';

// Cohort colors for multi-line chart
const COHORT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

// Convert ISO week to date string
function isoWeekToDate(isoWeek: string): string {
    const [yearStr, weekStr] = isoWeek.split('-W');
    const year = parseInt(yearStr);
    const week = parseInt(weekStr);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    return monday.toISOString().split('T')[0];
}

export const GrowthTab: React.FC = () => {
    const mindsetData = useQuery(api.admin_engagement_analytics.getMindsetScoreTrajectories);
    const xpData = useQuery(api.admin_engagement_analytics.getXpAndSkillStats);

    const isLoading = !mindsetData || !xpData;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    const xpVelocityChartData = xpData.weeklyXpVelocity.map(d => ({
        date: isoWeekToDate(d.week),
        count: d.avgXpPerUser,
    }));

    return (
        <div className="space-y-8">
            {/* Mindset Score Trajectories */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Mindset Score Trajectories</h3>
                <p className="text-sm text-muted-foreground">
                    Average composite mindset score by signup month cohort. Shows how users progress over time.
                </p>
                {mindsetData.cohorts.length === 0 ? (
                    <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
                        <p className="text-sm text-muted-foreground">No mindset score data available yet.</p>
                    </div>
                ) : (
                    <MindsetCohortChart cohorts={mindsetData.cohorts} />
                )}
            </div>

            {/* Skill Level Distribution */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Skill Level Distribution</h3>
                <DistributionHistogram
                    data={xpData.overallLevelDistribution}
                    title="Overall Level Distribution"
                    subtitle="Number of users at each overall level"
                    xAxisLabel="Level"
                />
                {Object.entries(xpData.perSkillDistribution).map(([skillName, levels]) => (
                    <HorizontalBarChart
                        key={skillName}
                        data={Object.entries(levels).map(([level, count]) => ({
                            label: `Level ${level}`,
                            value: count,
                        }))}
                        title={`${skillName} — Level Distribution`}
                        sortBy="label"
                    />
                ))}
            </div>

            {/* XP Velocity */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">XP Velocity</h3>
                <p className="text-sm text-muted-foreground">
                    Average XP earned per active user per week. Declining velocity may indicate engagement problems.
                </p>
                {xpVelocityChartData.length > 0 ? (
                    <DailyStatsChart
                        data={xpVelocityChartData}
                        title="Weekly Avg XP per Active User"
                        lineColor="#f59e0b"
                        yAxisLabel="Avg XP"
                        tooltipSuffix="XP per user"
                    />
                ) : (
                    <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
                        <p className="text-sm text-muted-foreground">No XP data available yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Multi-line SVG chart for cohort mindset trajectories
interface CohortTrajectory {
    cohortMonth: string;
    userCount: number;
    trajectory: Array<{ monthsAfterSignup: number; avgScore: number; sampleSize: number }>;
}

const MindsetCohortChart: React.FC<{ cohorts: CohortTrajectory[] }> = ({ cohorts }) => {
    const [dimensions, setDimensions] = useState({ width: 800, height: 350 });

    useEffect(() => {
        const updateDimensions = () => {
            const screenWidth = window.innerWidth;
            if (screenWidth < 640) setDimensions({ width: Math.min(screenWidth - 32, 400), height: 250 });
            else if (screenWidth < 1024) setDimensions({ width: Math.min(screenWidth - 64, 600), height: 300 });
            else setDimensions({ width: 800, height: 350 });
        };
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    const { width, height } = dimensions;
    const isMobile = width < 500;
    const padding = isMobile ? 50 : 60;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Find max months and score range
    const allMonths = cohorts.flatMap(c => c.trajectory.map(t => t.monthsAfterSignup));
    const maxMonth = Math.max(...allMonths, 1);
    const allScores = cohorts.flatMap(c => c.trajectory.map(t => t.avgScore));
    const minScore = Math.min(...allScores, 0);
    const maxScore = Math.max(...allScores, 100);
    const scoreRange = maxScore - minScore || 1;

    return (
        <div className="p-2 sm:p-2 rounded-lg border bg-transparent w-full overflow-x-auto">
            <h3 className="text-sm sm:text-lg font-semibold mb-2 sm:mb-4">Cohort Mindset Score Trajectories</h3>
            <div className="min-w-full">
                <svg width={width} height={height} className="overflow-visible max-w-full">
                    {/* Grid lines */}
                    {[0, 25, 50, 75, 100].map(val => {
                        if (val < minScore || val > maxScore + 10) return null;
                        const y = padding + chartHeight - ((val - minScore) / scoreRange) * chartHeight;
                        return (
                            <g key={val}>
                                <line x1={padding} y1={y} x2={padding + chartWidth} y2={y} stroke="#f3f4f6" strokeWidth="1" />
                                <text x={padding - 10} y={y + 4} textAnchor="end" className="text-xs fill-gray-600">{val}</text>
                            </g>
                        );
                    })}

                    {/* Axes */}
                    <line x1={padding} y1={padding + chartHeight} x2={padding + chartWidth} y2={padding + chartHeight} stroke="#e5e7eb" strokeWidth="2" />
                    <line x1={padding} y1={padding} x2={padding} y2={padding + chartHeight} stroke="#e5e7eb" strokeWidth="2" />

                    {/* Y-axis label */}
                    <text x={isMobile ? 15 : 20} y={padding + chartHeight / 2} textAnchor="middle"
                        className={isMobile ? "text-xs fill-gray-700" : "text-sm fill-gray-700"}
                        transform={`rotate(-90, ${isMobile ? 15 : 20}, ${padding + chartHeight / 2})`}>
                        Score
                    </text>

                    {/* X-axis labels */}
                    {Array.from({ length: Math.min(maxMonth + 1, 13) }, (_, i) => i).map(m => {
                        const x = padding + (m / maxMonth) * chartWidth;
                        return (
                            <text key={m} x={x} y={padding + chartHeight + 20} textAnchor="middle" className="text-xs fill-gray-600">
                                {m}m
                            </text>
                        );
                    })}

                    {/* Cohort lines */}
                    {cohorts.map((cohort, ci) => {
                        const color = COHORT_COLORS[ci % COHORT_COLORS.length];
                        const points = cohort.trajectory.map(t => ({
                            x: padding + (t.monthsAfterSignup / maxMonth) * chartWidth,
                            y: padding + chartHeight - ((t.avgScore - minScore) / scoreRange) * chartHeight,
                            ...t,
                        }));

                        const pathData = points.reduce((path, p, i) => `${path} ${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`, '');

                        return (
                            <g key={cohort.cohortMonth}>
                                <path d={pathData} fill="none" stroke={color} strokeWidth="2" opacity="0.8" />
                                {points.map((p, pi) => (
                                    <g key={pi}>
                                        <circle cx={p.x} cy={p.y} r={isMobile ? 3 : 4} fill={color} />
                                        <circle cx={p.x} cy={p.y} r={12} fill="transparent" className="cursor-pointer">
                                            <title>{`${cohort.cohortMonth} — Month ${p.monthsAfterSignup}: avg ${p.avgScore} (n=${p.sampleSize})`}</title>
                                        </circle>
                                    </g>
                                ))}
                            </g>
                        );
                    })}
                </svg>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-3 px-2">
                {cohorts.map((cohort, ci) => (
                    <div key={cohort.cohortMonth} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COHORT_COLORS[ci % COHORT_COLORS.length] }} />
                        <span className="text-xs text-muted-foreground">{cohort.cohortMonth} ({cohort.userCount} users)</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
